/**
 * filePreview – delad, säkerhetshärdad logik för filförhandsvisning.
 *
 * Bakgrund: uppladdade filers MIME-typ kommer från klienten som laddade upp
 * filen och sparas ordagrant. Byggs en blob-URL av den typen och renderas i en
 * iframe utan sandbox körs innehållet på appens origin — en fil som heter
 * "Kvalitetsplan.pdf" men har Content-Type text/html blir då lagrad XSS.
 *
 * Den här modulen är det enda stället där en förhandsvisnings-blob får skapas:
 *
 *  1. `resolvePreviewKind()` avgör sort och MIME ur en FAST VITLISTA — aldrig ur
 *     den lagrade eller serverskickade typen.
 *  2. `createSafePreviewUrl()` bygger om blobben med den vitlistade typen.
 *     Okänd sort får `application/octet-stream`, vilket alltid laddas ner och
 *     aldrig renderas inline.
 *  3. `PREVIEW_IFRAME_SANDBOX` sätts på varje kvarvarande förhandsvisnings-iframe.
 *     Innehåller medvetet INTE `allow-same-origin` — då vore sandlådan verkningslös.
 *  4. `UnsupportedPreviewNotice` är den gemensamma "kan inte visas"-vyn med
 *     nedladdningsknapp, så ingen vy behöver falla tillbaka på en naken iframe.
 *
 * Om PDF och iframes (verifierat i Chromium 2026-07-30, inte antaget):
 * webbläsarens inbyggda PDF-visare är ett plugin/MimeHandlerView som Chrome
 * vägrar starta i en sandboxad iframe — varken `allow-scripts` eller till och med
 * `allow-scripts allow-same-origin` hjälper, man får bara "trasig fil"-ikonen.
 * Därför renderas PDF inte längre i iframe alls, utan via den befintliga
 * `PdfViewer`-komponenten (pdf.js -> canvas). Det ger ingen HTML-parsning och
 * därmed ingen scriptkörning, och är dessutom samma PDF-vy som Dokument-,
 * Ritningar- och OpenBooks-modulerna redan använder.
 *
 * Ligger i utils/ eftersom de tre konsumenterna bor i olika komponentträd
 * (components/, components/ManagementSystem/documents/) och ingen av dem bör
 * importera från en annan modul-mapp.
 */
import React from 'react';

/** Filändelse -> { kind, mime }. Enda källan till hur något får renderas. */
const EXT_WHITELIST = {
  pdf: { kind: 'pdf', mime: 'application/pdf' },

  png: { kind: 'image', mime: 'image/png' },
  jpg: { kind: 'image', mime: 'image/jpeg' },
  jpeg: { kind: 'image', mime: 'image/jpeg' },
  gif: { kind: 'image', mime: 'image/gif' },
  webp: { kind: 'image', mime: 'image/webp' },
  bmp: { kind: 'image', mime: 'image/bmp' },
  svg: { kind: 'image', mime: 'image/svg+xml' },

  doc: { kind: 'docx', mime: 'application/msword' },
  docx: { kind: 'docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },

  xls: { kind: 'xlsx', mime: 'application/vnd.ms-excel' },
  xlsx: { kind: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },

  txt: { kind: 'text', mime: 'text/plain;charset=utf-8' },
  csv: { kind: 'text', mime: 'text/plain;charset=utf-8' },
  log: { kind: 'text', mime: 'text/plain;charset=utf-8' },
  md: { kind: 'text', mime: 'text/plain;charset=utf-8' },
};

/** Fallback när filnamnet saknar ändelse: grov matchning mot samma vitlista. */
function kindFromContentType(contentType) {
  const c = String(contentType || '').toLowerCase();
  if (!c) return null;
  if (c.includes('application/pdf')) return EXT_WHITELIST.pdf;
  if (c.includes('wordprocessingml')) return EXT_WHITELIST.docx;
  if (c.includes('application/msword')) return EXT_WHITELIST.doc;
  if (c.includes('spreadsheetml')) return EXT_WHITELIST.xlsx;
  if (c.includes('application/vnd.ms-excel')) return EXT_WHITELIST.xls;
  if (c.startsWith('image/')) {
    const sub = c.split(';')[0].trim().replace('image/', '');
    const hit = Object.values(EXT_WHITELIST).find((v) => v.kind === 'image' && v.mime === `image/${sub}`);
    return hit || null;
  }
  if (c.startsWith('text/plain')) return EXT_WHITELIST.txt;
  return null;
}

/** MIME som garanterar nedladdning i stället för inline-rendering. */
export const DOWNLOAD_MIME = 'application/octet-stream';

/**
 * Sandbox för de förhandsvisnings-iframes som är kvar (endast ren text).
 * Varken `allow-same-origin` eller `allow-scripts` ingår: iframen får en opak
 * origin utan scriptkörning och kan alltså varken läsa appens
 * cookies/localStorage eller göra credentialed fetch mot API:t som offret.
 * `allow-downloads` finns kvar så att "spara som" fungerar.
 */
export const PREVIEW_IFRAME_SANDBOX = 'allow-downloads';

function extFromName(name) {
  if (!name) return '';
  const last = String(name).trim().split('/').pop() || '';
  if (!last.includes('.')) return '';
  return (last.split('.').pop() || '').toLowerCase();
}

function extFromUrl(rawUrl) {
  if (!rawUrl) return '';
  try {
    const { pathname } = new URL(rawUrl, typeof window !== 'undefined' ? window.location.href : 'http://localhost');
    return extFromName(pathname.split('/').pop() || '');
  } catch {
    const clean = String(rawUrl).split(/[?#]/)[0];
    return extFromName(clean.split('/').pop() || '');
  }
}

/**
 * Avgör hur en fil får förhandsvisas.
 *
 * @param {{ filename?: string, url?: string, contentType?: string }} input
 * @returns {{ kind: string, mime: string }} kind är '' när filtypen inte får
 *   visas inline; mime är då `DOWNLOAD_MIME`.
 */
export function resolvePreviewKind({ filename, url, contentType } = {}) {
  const ext = extFromName(filename) || extFromUrl(url);
  const byExt = ext ? EXT_WHITELIST[ext] : null;
  const hit = byExt || (ext ? null : kindFromContentType(contentType));
  if (!hit) return { kind: '', mime: DOWNLOAD_MIME };
  return { kind: hit.kind, mime: hit.mime };
}

/**
 * Bygg om en hämtad blob med en vitlistad MIME-typ.
 * @param {Blob} blob
 * @param {string} mime
 * @returns {Blob}
 */
export function toSafePreviewBlob(blob, mime) {
  return new Blob([blob], { type: mime || DOWNLOAD_MIME });
}

/**
 * Skapa en object-URL vars typ kommer ur vitlistan, aldrig ur blobbens egen typ.
 * @param {Blob} blob
 * @param {string} mime
 * @returns {string}
 */
export function createSafePreviewUrl(blob, mime) {
  return URL.createObjectURL(toSafePreviewBlob(blob, mime));
}

/** Sorter som får renderas i en iframe (PDF går till pdf.js, bilder till img). */
export function isIframeKind(kind) {
  return kind === 'text';
}

/**
 * Gemensam "kan inte förhandsvisas"-vy. Ersätter catch-all-iframen.
 *
 * @param {{ filename?: string, downloadHref?: string, onDownload?: Function, compact?: boolean }} props
 */
export function UnsupportedPreviewNotice({ filename, downloadHref, onDownload, compact = false }) {
  const ext = extFromName(filename);
  const btnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 16px',
    borderRadius: 8,
    border: '1px solid var(--kp-border, #E4E9F0)',
    background: 'var(--kp-white, #FFFFFF)',
    color: 'var(--kp-text, #0F172A)',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
  };
  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: compact ? '24px 16px' : '40px 24px',
        textAlign: 'center',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="32"
        height="32"
        fill="none"
        stroke="var(--kp-text-secondary, #64748B)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M9 13h6" />
      </svg>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--kp-text, #0F172A)' }}>
        Förhandsvisning stöds inte för den här filtypen
      </div>
      <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--kp-text-secondary, #64748B)', maxWidth: 380 }}>
        {filename ? `${filename}${ext ? ` (.${ext})` : ''} kan inte visas direkt i webbläsaren. ` : ''}
        Ladda ner filen och öppna den i rätt program.
      </div>
      {(downloadHref || onDownload) && (
        downloadHref ? (
          <a href={downloadHref} download={filename || 'dokument'} style={btnStyle} onClick={(e) => e.stopPropagation()}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 2v8" />
              <path d="M4.5 7.5L8 11l3.5-3.5" />
              <path d="M2.5 13h11" />
            </svg>
            Ladda ner
          </a>
        ) : (
          <button type="button" onClick={onDownload} style={btnStyle}>
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 2v8" />
              <path d="M4.5 7.5L8 11l3.5-3.5" />
              <path d="M2.5 13h11" />
            </svg>
            Ladda ner
          </button>
        )
      )}
    </div>
  );
}
